import type { GameState, TriAngerAction } from "@cloudnest/tri-anger-engine";
import { applyAction } from "@cloudnest/tri-anger-engine";
import { triAngerEventToAction } from "./event-adapter";
import type { TriAngerReplayFixtureEvent } from "./replay-fixtures.generated";

export interface TriAngerReplaySnapshot {
  readonly index: number;
  readonly event: TriAngerReplayFixtureEvent;
  readonly action: TriAngerAction | null;
  readonly state: GameState;
  readonly note: string;
}

function requireState(state: GameState | null, eventType: string): GameState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function eventNote(state: GameState, eventType: string): string {
  if (state.phase === "GAME_OVER") {
    return `Game over: ${state.victoryCondition ?? "unknown"}`;
  }
  return `${eventType} accepted; phase ${state.phase}, turn ${state.turn}`;
}

export function buildTriAngerReplaySnapshots(
  events: readonly TriAngerReplayFixtureEvent[],
): readonly TriAngerReplaySnapshot[] {
  const snapshots: TriAngerReplaySnapshot[] = [];
  let state: GameState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = (event.payload as { readonly state: GameState }).state;
      snapshots.push({
        index: snapshots.length,
        event,
        action: null,
        state,
        note: "Initial canonical public state.",
      });
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = triAngerEventToAction(event.eventType, event.payload);
    const result = applyAction(current, action);
    if (!result.ok) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.validation.error ?? "Validation failed"
        }`,
      );
    }

    state = result.state;
    snapshots.push({
      index: snapshots.length,
      event,
      action,
      state,
      note: eventNote(state, event.eventType),
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Tri-Anger event log");
  }

  return snapshots;
}
